import useSWR from 'swr';
import chunk from 'lodash/chunk';
import { sdk } from '@/sdk';
import { GetProductQuery, ProductQueryQuery } from '@/sdk/composable-commerce-b2b/types/queries/ProductQueries';
import { Category, Product } from '@shared/types/product';

const MAX_IDS = 50;

export const useProducts = (query: ProductQueryQuery | undefined, enabled = true) => {
  const key = enabled ? ['/action/product/query', query] : null;

  const fetcher = async () => {
    if (!query) return { items: [] as Product[], isError: false };

    if (query.productIds && query.productIds.length > MAX_IDS) {
      const idChunks = chunk(query.productIds, MAX_IDS);

      const results = await Promise.all(
        idChunks.map((ids) =>
          sdk.composableCommerce.product.query({
            ...query,
            productIds: ids,
          }),
        ),
      );

      return {
        items: results.flatMap((r) => (r.isError ? [] : (r.data?.items as Product[]) ?? [])),
        isError: results.some((r) => r.isError),
      };
    }

    const res = await sdk.composableCommerce.product.query(query);
    return {
      items: res.isError ? [] : (res.data?.items as Product[]),
      isError: res.isError,
    };
  };

  const { data } = useSWR(key, fetcher);

  return data?.isError ? [] : data?.items ?? [];
};

export const useProduct = (query: GetProductQuery | undefined, enabled = true) => {
  const key = enabled && query ? ['/action/product/getProduct', query] : null;

  const fetcher = () => sdk.composableCommerce.product.getProduct(query as GetProductQuery);

  const { data } = useSWR(key, fetcher);

  return data?.isError ? undefined : data?.data ?? undefined;
};

export const useProductsByCategory = (categories: Category[], enabled = true) => {
  const { data } = useSWR(enabled ? ['/action/product/querySearch', categories] : null, async () => {
    const productsPromises = categories.map((category) => {
      return sdk.composableCommerce.product.querySearch({
        category: category.categoryId,
        isCommercialOperation: category.isCommercialOperation ?? false,
        limit: 0,
      });
    });

    const productsArraySolved = await Promise.all(productsPromises);
    return productsArraySolved.map((products) => (products.isError ? 0 : products.data.total ?? 0));
  });

  return data ?? [];
};
